import { useEffect, useRef, useState } from 'react';

import { useI18n } from '../i18n/index.js';
import { ACCEPT_ATTRIBUTE, ImageFileError, decodeImage } from '../lib/imageFile.js';
import { ImageCropper } from './ImageCropper.js';
import { PencilIcon, PlusIcon } from './icons.js';
import { Spinner } from './Spinner.js';
import styles from './ImagePicker.module.css';

interface ImagePickerProps {
  /** 지금 보여줄 사진. 이미 올라간 주소이거나, 로컬 Blob의 objectURL */
  previewUrl: string | null;
  /** 고르고 줄인 결과. 저장할 때 올리거나 로컬에 담는다 */
  onPick: (blob: Blob) => void;
  onClear: () => void;
  error?: string | null;
  disabled?: boolean;
  /** 기본은 Pick의 "사진". 프로필 사진처럼 다른 것을 고를 때 바꾼다 */
  label?: string;
  /** 프로필 사진은 동그랗게 보여준다 — 붙는 자리와 같은 모양이어야 한다 */
  shape?: 'rect' | 'circle';
}

/**
 * 사진 고르기.
 *
 * 주소를 붙여넣게 하지 않는다 — 남의 서버 이미지를 끌어다 쓰게 되고(어느 날
 * 사라진다), 사람들은 애초에 자기 사진을 올리고 싶어 한다.
 *
 * 고르는 즉시 **브라우저에서 줄인다.** 폰 사진 한 장이 5~10MB인데 격자 한 칸은
 * 150px이다. 줄이면서 EXIF가 빠져 사진에 박힌 GPS 좌표도 같이 사라진다.
 */
export function ImagePicker({
  previewUrl,
  onPick,
  onClear,
  error = null,
  disabled = false,
  label,
  shape = 'rect',
}: ImagePickerProps) {
  const { t } = useI18n();
  const inputRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);
  /** 자르기 중인 사진. 있으면 자르기 창이 떠 있다 */
  const [cropping, setCropping] = useState<ImageBitmap | null>(null);
  const alive = useRef(true);

  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);

  const handleFile = async (file: File | undefined) => {
    if (file === undefined) {
      return;
    }
    setBusy(true);
    setLocalError(null);
    try {
      /*
       * 바로 줄여서 올리지 않는다. **어디를 남길지는 올리는 사람이 정한다** —
       * 격자 칸이 4:5라 그냥 넣으면 브라우저가 가운데를 기준으로 잘라내는데,
       * 정작 보여주고 싶은 것이 가운데 있으리란 법이 없다.
       */
      const decoded = await decodeImage(file);
      if (alive.current) {
        setCropping(decoded);
      } else {
        decoded.close();
      }
    } catch (cause) {
      if (alive.current) {
        setLocalError(
          t(
            cause instanceof ImageFileError && cause.reason === 'tooLarge'
              ? 'image.tooLarge'
              : 'image.unreadable',
          ),
        );
      }
    } finally {
      if (alive.current) {
        setBusy(false);
      }
      // 같은 파일을 다시 고를 수 있게 비운다 — 안 비우면 change가 안 난다
      if (inputRef.current !== null) {
        inputRef.current.value = '';
      }
    }
  };

  const shown = error ?? localError;

  /** 자르기가 끝났든 취소됐든, 비트맵은 **반드시** 놓아준다 */
  const closeCropper = (): void => {
    cropping?.close();
    setCropping(null);
  };

  return (
    <div className={styles.field}>
      <ImageCropper
        bitmap={cropping}
        // 격자 한 칸과 **같은 비율**이어야 화면에서 본 대로 잘린다
        aspect={shape === 'circle' ? 1 : 4 / 5}
        round={shape === 'circle'}
        onCancel={closeCropper}
        onDone={(blob) => {
          closeCropper();
          onPick(blob);
        }}
        onError={(cause) => {
          closeCropper();
          setLocalError(
            t(
              cause instanceof ImageFileError && cause.reason === 'tooLarge'
                ? 'image.tooLarge'
                : 'image.unreadable',
            ),
          );
        }}
      />
      <span className={styles.label}>{label ?? t('picks.itemImage')}</span>

      <input
        ref={inputRef}
        className="srOnly"
        type="file"
        accept={ACCEPT_ATTRIBUTE}
        disabled={disabled || busy}
        onChange={(event) => void handleFile(event.target.files?.[0])}
      />

      <button
        type="button"
        className={[
          styles.drop,
          previewUrl === null ? styles.dropEmpty : '',
          shape === 'circle' ? styles.circle : '',
          shown !== null ? styles.invalid : '',
        ]
          .filter(Boolean)
          .join(' ')}
        disabled={disabled || busy}
        /*
          사진이 있을 때는 아이콘만 남아 버튼 안에 읽을 글자가 없다.
          이름을 여기서 달아 주지 않으면 스크린 리더에 "버튼"으로만 들린다.
        */
        aria-label={previewUrl === null ? undefined : t('image.change')}
        onClick={() => inputRef.current?.click()}
      >
        {busy ? (
          <Spinner label={t('image.processing')} />
        ) : previewUrl !== null ? (
          <>
            <img className={styles.preview} src={previewUrl} alt="" />
            <span className={styles.change}>
              <PencilIcon size={16} />
            </span>
          </>
        ) : (
          <span className={styles.empty}>
            <span className={styles.emptyPlus}>
              <PlusIcon size={22} />
            </span>
            <span>{t('image.choose')}</span>
          </span>
        )}
      </button>

      {previewUrl !== null && !busy ? (
        <button type="button" className={styles.remove} onClick={onClear} disabled={disabled}>
          {t('image.remove')}
        </button>
      ) : null}

      {shown !== null ? <p className={styles.error}>{shown}</p> : null}
    </div>
  );
}
